import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  e2eLocators,
  fakeSecretKey
} from '@components/storage-accounts/add-storage-account-wizard/credential-forms/credential-forms.constants';
import {
  getDomainIdentifiers,
  getKeystoneVersions,
  getProjectIdentifiers,
  getTenantIdentifiers
} from '@components/storage-accounts/add-storage-account-wizard/credential-forms/openstack/credential-form-openstack.constants';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import {
  CardGroupComponent,
  CheckboxComponent,
  FormGroupComponent,
  InputComponent,
  InputLabelComponent,
  InputPasswordComponent,
  SelectComponent
} from 'mbs-ui-kit';
import { StorageType } from 'mbs-ui-shared@models';
import { DomainIdentifier, KeystoneVersion, OpenstackCredentialForm, ProjectIdentifier, TenantIdentifier } from '../credential-forms.model';

@UntilDestroy()
@Component({
  selector: 'mbs-credential-form-openstack',
  templateUrl: './credential-form-openstack.component.html',
  styleUrls: ['../credential-forms.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CardGroupComponent,
    CheckboxComponent,
    CommonModule,
    FormGroupComponent,
    I18NextPipe,
    InputComponent,
    InputLabelComponent,
    InputPasswordComponent,
    ReactiveFormsModule,
    SelectComponent
  ]
})
export class CredentialFormOpenstackComponent implements OnInit {
  private i18n = inject(I18NextPipe);

  @Input() form: OpenstackCredentialForm;

  public readonly StorageType = StorageType;
  public readonly KeystoneVersion = KeystoneVersion;
  public readonly TenantIdentifier = TenantIdentifier;
  public readonly DomainIdentifier = DomainIdentifier;
  public readonly ProjectIdentifier = ProjectIdentifier;
  public readonly keystoneVersions = getKeystoneVersions(this.i18n);
  public readonly tenantIdentifiers = getTenantIdentifiers(this.i18n);
  public readonly domainIdentifiers = getDomainIdentifiers(this.i18n);
  public readonly projectIdentifiers = getProjectIdentifiers(this.i18n);
  public readonly fakeSecretKey = fakeSecretKey;
  public readonly e2eLocators = e2eLocators;

  ngOnInit() {
    this.keystoneVersionControl.valueChanges.pipe(untilDestroyed(this)).subscribe((val) => {
      if (val === KeystoneVersion.None) {
        this.resetTenant();
        this.resetDomain();
        this.resetScope();
      }

      if (val === KeystoneVersion.Second) {
        this.resetDomain();
        this.resetScope();
        this.turnOnTenant();
      }

      if (val === KeystoneVersion.Third) {
        this.resetTenant();
        this.turnOnDomain();
      }
    });

    this.useScopeControl.valueChanges.pipe(untilDestroyed(this)).subscribe((val) => {
      if (val) {
        this.turnOnProject();
      } else {
        this.resetProject();
      }
    });
  }

  private resetTenant() {
    this.tenantIdentifierControl.patchValue(null);
    this.tenantIdentifierControl.clearValidators();
    this.tenantIdentifierControl.updateValueAndValidity();
    this.tenantControl.patchValue(null);
    this.tenantControl.clearValidators();
    this.tenantControl.updateValueAndValidity();
  }

  private resetDomain() {
    this.domainIdentifierControl.patchValue(null);
    this.domainIdentifierControl.clearValidators();
    this.domainIdentifierControl.updateValueAndValidity();
    this.domainControl.patchValue(null);
    this.domainControl.clearValidators();
    this.domainControl.updateValueAndValidity();
  }

  private resetProject() {
    this.projectIdentifierControl.patchValue(null);
    this.projectIdentifierControl.clearValidators();
    this.projectIdentifierControl.updateValueAndValidity();
    this.projectControl.patchValue(null);
    this.projectControl.clearValidators();
    this.projectControl.updateValueAndValidity();
  }

  private resetScope() {
    this.useScopeControl.patchValue(false);
    this.useScopeControl.updateValueAndValidity();
  }

  private turnOnTenant() {
    this.tenantIdentifierControl.patchValue(TenantIdentifier.Name);
    this.tenantIdentifierControl.setValidators([Validators.required]);
    this.tenantIdentifierControl.updateValueAndValidity();
    this.tenantControl.patchValue(null);
    this.tenantControl.setValidators([Validators.required]);
    this.tenantControl.updateValueAndValidity();
  }

  private turnOnDomain() {
    this.domainIdentifierControl.patchValue(DomainIdentifier.Name);
    this.domainIdentifierControl.setValidators([Validators.required]);
    this.domainIdentifierControl.updateValueAndValidity();
    this.domainControl.patchValue(null);
    this.domainControl.setValidators([Validators.required]);
    this.domainControl.updateValueAndValidity();
  }

  private turnOnProject() {
    this.projectIdentifierControl.patchValue(ProjectIdentifier.Name);
    this.projectIdentifierControl.setValidators([Validators.required]);
    this.projectIdentifierControl.updateValueAndValidity();
    this.projectControl.patchValue(null);
    this.projectControl.setValidators([Validators.required]);
    this.projectControl.updateValueAndValidity();
  }

  public get userNameControl(): FormControl {
    return this.form.get('userName') as FormControl;
  }

  public get apiKeyControl(): FormControl {
    return this.form.get('apiKey') as FormControl;
  }

  public get endpointControl(): FormControl {
    return this.form.get('endpoint') as FormControl;
  }

  public get keystoneVersionControl(): FormControl {
    return this.form.get('keystoneVersion') as FormControl;
  }

  public get domainIdentifierControl(): FormControl {
    return this.form.get('domainIdentifier') as FormControl;
  }

  public get domainControl(): FormControl {
    return this.form.get('domain') as FormControl;
  }

  public get useScopeControl(): FormControl {
    return this.form.get('useScope') as FormControl;
  }

  public get projectIdentifierControl(): FormControl {
    return this.form.get('projectIdentifier') as FormControl;
  }

  public get projectControl(): FormControl {
    return this.form.get('project') as FormControl;
  }

  public get tenantIdentifierControl(): FormControl {
    return this.form.get('tenantIdentifier') as FormControl;
  }

  public get tenantControl(): FormControl {
    return this.form.get('tenant') as FormControl;
  }

  public get privateStorageControl(): FormControl {
    return this.form.get('isPrivateStorage') as FormControl;
  }

  public get useInternalUrlsControl(): FormControl {
    return this.form.get('useInternalUrls') as FormControl;
  }

  public get ignoreCertificatesControl(): FormControl {
    return this.form.get('ignoreCertificates') as FormControl;
  }

  public get keystoneVersion(): KeystoneVersion {
    return this.keystoneVersionControl.value;
  }

  public get tenantIdentifier(): TenantIdentifier {
    return this.form.get('tenantIdentifier').value;
  }

  public get domainIdentifier(): DomainIdentifier {
    return this.domainIdentifierControl.value;
  }

  public get projectIdentifier(): ProjectIdentifier {
    return this.form.get('projectIdentifier').value;
  }

  public get useScope(): boolean {
    return this.form.get('useScope').value;
  }
}
